<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const mapElement = ref(null);
const points = ref([]);
const summary = ref({
  eligible: 0,
  mapped: 0,
  pending: 0,
  failed: 0,
  displayed: 0,
});
const search = ref('');
const isLoading = ref(true);
const isSyncing = ref(false);
let map;
let markerLayer;

const filteredPoints = computed(() => {
  const query = search.value.trim().toLocaleLowerCase('pt-BR');
  if (!query) return points.value;

  return points.value.filter(point =>
    [point.name, point.address, point.city, point.state, point.zipCode]
      .filter(Boolean)
      .some(value => String(value).toLocaleLowerCase('pt-BR').includes(query))
  );
});

const loadLeaflet = () => {
  if (window.L) return Promise.resolve(window.L);

  if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = LEAFLET_CSS;
    link.crossOrigin = '';
    document.head.appendChild(link);
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L), {
        once: true,
      });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.crossOrigin = '';
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const escapeHtml = value =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const renderMarkers = () => {
  if (!map || !window.L) return;

  markerLayer.clearLayers();
  const bounds = [];
  filteredPoints.value.forEach(point => {
    const latitude = Number(point.latitude);
    const longitude = Number(point.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

    const contactUrl = `/app/accounts/${route.params.accountId}/contacts/${point.id}`;
    const location = [point.address, point.city, point.state, point.zipCode]
      .filter(Boolean)
      .join(' · ');
    const marker = window.L.marker([latitude, longitude]);
    marker.bindPopup(`
      <div class="talkvia-contact-map-popup">
        <strong>${escapeHtml(point.name)}</strong>
        <span>${escapeHtml(location)}</span>
        <a href="${contactUrl}">${escapeHtml(t('CONTACTS_MAP.OPEN_CONTACT'))}</a>
      </div>
    `);
    marker.addTo(markerLayer);
    bounds.push([latitude, longitude]);
  });

  if (bounds.length) {
    map.fitBounds(bounds, { maxZoom: 14, padding: [36, 36] });
  } else {
    map.setView([-14.235, -51.925], 4);
  }
};

const initializeMap = async () => {
  await loadLeaflet();
  await nextTick();
  if (!mapElement.value) return;

  map = window.L.map(mapElement.value, { minZoom: 3 }).setView(
    [-14.235, -51.925],
    4
  );
  window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19,
  }).addTo(map);
  markerLayer = window.L.layerGroup().addTo(map);
  renderMarkers();
};

const fetchMap = async () => {
  isLoading.value = true;
  try {
    const { data } = await ContactAPI.getMap();
    points.value = data.points || [];
    summary.value = { ...summary.value, ...(data.summary || {}) };
    await nextTick();
    map?.invalidateSize();
    renderMarkers();
  } catch {
    useAlert(t('CONTACTS_MAP.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const syncMap = async () => {
  isSyncing.value = true;
  try {
    const { data } = await ContactAPI.syncMap();
    useAlert(t('CONTACTS_MAP.SYNC_SCHEDULED', { count: data.scheduled || 0 }));
    await fetchMap();
  } catch {
    useAlert(t('CONTACTS_MAP.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

watch(filteredPoints, renderMarkers);

onMounted(async () => {
  await fetchMap();
  try {
    await initializeMap();
    renderMarkers();
  } catch {
    useAlert(t('CONTACTS_MAP.LOAD_ERROR'));
  }
});

onBeforeUnmount(() => {
  map?.remove();
  map = null;
});
</script>

<template>
  <div class="flex h-full flex-col overflow-y-auto bg-n-surface-1">
    <header
      class="sticky top-0 z-20 border-b border-n-weak bg-n-surface-1 px-6 py-5"
    >
      <div
        class="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3"
      >
        <div class="flex items-center gap-3">
          <Button
            icon="i-lucide-arrow-left"
            color="slate"
            variant="ghost"
            size="sm"
            @click="
              router.push({
                name: 'contacts_dashboard_index',
                query: { page: 1 },
              })
            "
          />
          <div>
            <h1 class="text-xl font-medium text-n-slate-12">
              {{ t('CONTACTS_MAP.TITLE') }}
            </h1>
            <p class="text-sm text-n-slate-10">
              {{ t('CONTACTS_MAP.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <Button
          :label="t('CONTACTS_MAP.SYNC')"
          icon="i-lucide-refresh-cw"
          size="sm"
          :is-loading="isSyncing"
          :disabled="isSyncing"
          @click="syncMap"
        />
      </div>
    </header>

    <main class="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-4 p-6">
      <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div
          v-for="item in [
            { label: t('CONTACTS_MAP.ELIGIBLE'), value: summary.eligible },
            { label: t('CONTACTS_MAP.MAPPED'), value: summary.mapped },
            { label: t('CONTACTS_MAP.PENDING'), value: summary.pending },
            { label: t('CONTACTS_MAP.FAILED'), value: summary.failed },
          ]"
          :key="item.label"
          class="rounded-xl border border-n-weak bg-n-surface-2 p-4"
        >
          <span class="text-xs text-n-slate-10">{{ item.label }}</span>
          <strong class="mt-1 block text-2xl font-semibold text-n-slate-12">{{
            item.value
          }}</strong>
        </div>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-surface-2 p-4">
        <Input
          v-model="search"
          type="search"
          :placeholder="t('CONTACTS_MAP.SEARCH')"
          class="mb-4 max-w-md"
        />
        <div
          v-if="isLoading"
          class="flex h-[32rem] items-center justify-center"
        >
          <Spinner />
        </div>
        <div
          v-else-if="!points.length"
          class="flex h-[32rem] flex-col items-center justify-center gap-2 text-center"
        >
          <span class="i-lucide-map-pin-off size-8 text-n-slate-9" />
          <strong class="text-n-slate-12">{{
            t('CONTACTS_MAP.EMPTY_TITLE')
          }}</strong>
          <span class="max-w-lg text-sm text-n-slate-10">{{
            t('CONTACTS_MAP.EMPTY_DESCRIPTION')
          }}</span>
        </div>
        <div
          v-show="!isLoading && points.length"
          ref="mapElement"
          class="h-[32rem] w-full overflow-hidden rounded-lg border border-n-weak"
        />
      </div>
    </main>
  </div>
</template>

<style>
.talkvia-contact-map-popup {
  display: flex;
  min-width: 190px;
  flex-direction: column;
  gap: 4px;
}

.talkvia-contact-map-popup a {
  margin-top: 4px;
  font-weight: 600;
  text-decoration: underline;
}
</style>
